import React, {useState, useEffect} from 'react'

interface VCardPreviewProps {
  vcfUrl?: string // URL of the vCard (.vcf) file
  file?: File // File object (the .vcf file uploaded by the user)
}

// Define an interface to represent the parsed vCard data
interface VCardData {
  fullName?: string
  firstName?: string
  lastName?: string
  nickname?: string
  phoneNumbers?: string[]
  emails?: string[]
  photo?: string
}

const VCardPreview: React.FC<VCardPreviewProps> = ({vcfUrl, file}) => {
  const [vCardData, setVCardData] = useState<VCardData | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  function parseVCard(vCardText: string): VCardData {
    const vCardData: VCardData = {
      phoneNumbers: [],
      emails: [],
    }

    const lines = vCardText.split(/\r?\n/)

    lines.forEach((line) => {
      if (!line.includes(':') || line.startsWith('BEGIN') || line.startsWith('END')) return

      const [rawKey, ...valueParts] = line.split(':')
      const value = valueParts.join(':').trim()
      const key = rawKey.split(';')[0] // Strip off any additional parameters

      switch (key) {
        case 'FN':
          vCardData.fullName = value
          break
        case 'N':
          const [lastName, firstName] = value.split(';')
          vCardData.firstName = firstName
          vCardData.lastName = lastName
          break
        case 'NICKNAME':
          vCardData.nickname = value
          break
        case 'TEL':
          vCardData.phoneNumbers?.push(value)
          break
        case 'EMAIL':
          vCardData.emails?.push(value)
          break
        case 'PHOTO':
          vCardData.photo = value
          break
        default:
          break
      }
    })

    return vCardData
  }

  // Fetch vCard from URL or File object
  useEffect(() => {
    const fetchVCardData = async () => {
      setLoading(true)
      setError('')

      try {
        let vcfContent = ''

        if (vcfUrl) {
          const response = await fetch(vcfUrl)
          vcfContent = await response.text()
        } else if (file) {
          const fileReader = new FileReader()
          fileReader.onload = () => {
            vcfContent = fileReader.result as string
            const parsedData = parseVCard(vcfContent)
            setVCardData(parsedData)
          }
          fileReader.readAsText(file)
          return
        }
        const parsedData = parseVCard(vcfContent)
        setVCardData(parsedData)
      } catch (err) {
        console.error(err)
        setError('Error loading vCard.')
      } finally {
        setLoading(false)
      }
    }

    fetchVCardData()
  }, [vcfUrl, file])

  if (loading) {
    return <div>Loading vCard...</div>
  }

  if (error) {
    return <div>{error}</div>
  }

  if (!vCardData) {
    return <div>No vCard data available.</div>
  }

  // Avatar with initials
  const initials = vCardData.fullName
    ? vCardData.fullName
        .split(' ')
        .map((name: string) => name.charAt(0).toUpperCase())
        .join('')
    : ''

  return (
    <div
      className='card shadow-sm rounded'
      style={{
        maxWidth: '400px',
        margin: '10px auto',
        padding: '15px ',
        backgroundColor: '#f7f8fa',
        borderRadius: '12x !important',
      }}
    >
      <div className='d-flex align-items-start'>
        {/* Left side: vCard Information */}
        <div className='me-3' style={{flex: 1}}>
          <h6 className='card-title'>{vCardData.fullName}</h6>
          <p>{vCardData.phoneNumbers?.filter((e) => e)?.join(',')}</p>
        </div>

        {/* Right side: Avatar with initials */}
        <div
          className='d-flex justify-content-center align-items-center'
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            backgroundColor: '#007bff',
            color: '#fff',
            fontSize: '18px',
            fontWeight: 'bold',
            textAlign: 'center',
          }}
        >
          {initials || 'N/A'}
        </div>
      </div>
    </div>
  )
}

export default VCardPreview
